<template>
  <div class="container page" :class="{ 'pl-0 pt-0 pr-0': $vuetify.breakpoint.xsOnly }">
    <template v-if="error || deleteError">
      <GenericError />
    </template>

    <template v-else>
      <div v-if="isLoading || deleteLoading" class="content-spinner">
        <v-progress-circular indeterminate size="50" color="primary"></v-progress-circular>
      </div>

      <div v-else-if="items.length > 0">
        <v-btn v-if="isShipowner" fixed dark fab bottom right color="warning" @click="showUserDialog = true">
          <v-icon>mdi-plus</v-icon>
        </v-btn>

        <template>
          <v-data-table
            class="table-pointer"
            :headers="headers"
            :items="items"
            :options.sync="options"
            :loading="isLoading"
            :server-items-length="totalItems"
            :footer-props="{ 'items-per-page-options': [5, 10, 25, 100] }"
            @click:row="show($event.user)"
          >
            <template v-slot:item.user.image="{ item }">
              <v-avatar size="32"
                ><img
                  :src="
                    item.user.image ? item.user.image.contentUrl + '?x-request=html' : require('@/assets/no-img.svg')
                  "
                  :alt="item.user.firstName"
              /></v-avatar>
            </template>

            <template v-slot:item.user.firstName="{ item }">
              {{ item.user.firstName }} {{ item.user.lastName }}
            </template>

            <template v-slot:item.organizationRole.name="{ item }">
              <div class="text-no-wrap">
                {{
                  item.user.emailVerified
                    ? $t('message.' + item.organizationRole.name)
                    : $t('message.accept_invitation_pending')
                }}
              </div>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-row v-if="isShipowner" no-gutters align="center" justify="end">
                <v-btn icon class="mx-0" @click.stop="edit(item.user)">
                  <v-icon color="grey darken-1">mdi-pencil</v-icon>
                </v-btn>
              </v-row>
            </template>
          </v-data-table>
        </template>

        <v-dialog v-model="deleteConfirmShow" max-width="400">
          <v-card v-if="selectedForDelete">
            <v-card-title class="headline"
              >Delete {{ selectedForDelete.user.firstName }} {{ selectedForDelete.user.lastName }}?</v-card-title
            >
            <v-card-text>
              Are you sure you want to delete
              {{ selectedForDelete.user.firstName }}
              {{ selectedForDelete.user.lastName }}? <br /><br />
              The deleted data <strong>can't be recovered</strong>
            </v-card-text>
            <v-card-actions>
              <div class="flex-grow-1"></div>
              <v-btn color="grey darken-1" text @click.native="onDeleteCancel">Cancel</v-btn>
              <v-btn color="red" text @click.native="onDeleteConfirm">Delete</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>

      <div v-else class="d-flex fill-height text-center align-center justify-center" style="flex: 1">
        <div>
          <v-icon color="grey lighten-2" size="150px">mdi-plus-circle-outline</v-icon>
          <h1 class="headline">Create your first entry</h1>
          <p class="subheading">
            There are no elements yet.
          </p>
          <v-btn color="primary" @click="showUserDialog = true">
            Create first entry
          </v-btn>
        </div>
      </div>

      <v-snackbar v-if="localCreatedItem" v-model="snackbar">
        {{ localCreatedItem.user.firstName }}
        {{ localCreatedItem.user.lastName }} successfully added.
        <v-btn text color="warning" @click.native="snackbar = false">Close</v-btn>
      </v-snackbar>
      <v-snackbar v-if="localUpdatedItem" v-model="snackbar">
        {{ localUpdatedItem.id }} successfully updated.
        <v-btn text color="warning" @click.native="snackbar = false">Close</v-btn>
      </v-snackbar>
      <v-snackbar v-if="localDeletedItem" v-model="snackbar">
        {{ localDeletedItem.id }} has been deleted.
        <v-btn text color="warning" @click.native="snackbar = false">Close</v-btn>
      </v-snackbar>
    </template>

    <UserDialog
      :show="showUserDialog"
      :user="selectedUser"
      :organization="organization"
      :force-edit-mode="forceEditMode"
      @close="showUserDialog = false"
    ></UserDialog>
  </div>
</template>

<script>
import GenericError from '@/components/error/GenericError'
import { mapActions, mapGetters } from 'vuex'
import UserDialog from '@/components/user/dialog/Dialog'

export default {
  components: {
    UserDialog,
    GenericError
  },

  props: {
    organization: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    forceEditMode: false,
    selectedUser: null,
    showUserDialog: false,
    organizationId: null,
    isShipowner: false,
    snackbar: false,
    localDeletedItem: null,
    localUpdatedItem: null,
    localCreatedItem: null,
    deleteConfirmShow: false,
    selectedForDelete: null,
    options: {
      page: 1,
      sortBy: ['user.firstName'],
      sortDesc: [true],
      itemsPerPage: 25
    },
    headers: []
  }),

  computed: {
    ...mapGetters({
      deletedItem: 'roleinorganization/del/deleted',
      error: 'roleinorganization/list/error',
      items: 'roleinorganization/list/items',
      totalItems: 'roleinorganization/list/totalItems',
      isLoading: 'roleinorganization/list/isLoading',
      view: 'roleinorganization/list/view',
      createdItem: 'roleinorganization/create/created',
      updatedItem: 'roleinorganization/update/updated',
      deleteError: 'roleinorganization/del/error',
      deleteLoading: 'roleinorganization/del/isLoading'
    })
  },

  watch: {
    deletedItem: function(deleted) {
      if (deleted) {
        this.localDeletedItem = this.deletedItem
        this.snackbar = true
        this.getPaginatedPage(this.options)
        this.reset()
      }
    },

    showUserDialog: function(showUserDialog) {
      if (false === showUserDialog) {
        this.selectedUser = null
        this.forceEditMode = false
      }
    },

    options: {
      handler: function(options, optionsOld) {
        if (!options) {
          return
        }
        if (
          options.itemsPerPage !== optionsOld.itemsPerPage ||
          options.page !== optionsOld.page ||
          options.sortBy !== optionsOld.sortBy ||
          options.sortDesc !== optionsOld.sortDesc
        ) {
          this.getPaginatedPage(options)
        }
      },
      deep: true
    }
  },

  created() {
    this.organizationId = this.organization['@id']
    this.getPaginatedPage(this.options)
    this.isShipowner = this.$security.isGranted(this.organizationId, 'ROLE_SHIPOWNER')

    this.headers = [
      { text: '', value: 'user.image', width: 1, sortable: false },
      {
        text: this.$t('message.employee'),
        value: 'user.firstName',
        sortable: false,
        width: '100%'
      },
      {
        text: this.$t('message.role'),
        value: 'organizationRole.name',
        sortable: false
      },
      {
        text: this.$t('message.actions'),
        value: 'actions',
        sortable: false,
        align: 'right'
      }
    ]
  },

  mounted() {
    if (this.deletedItem || this.updatedItem || this.createdItem) {
      this.localDeletedItem = this.deletedItem
      this.localUpdatedItem = this.updatedItem
      this.localCreatedItem = this.createdItem

      this.snackbar = true
    }
    this.reset()
  },

  methods: {
    selectForDelete(item) {
      this.selectedForDelete = item
      this.deleteConfirmShow = true
    },

    onDeleteConfirm() {
      this.deleteConfirmShow = false
      this.deleteItem(this.selectedForDelete)
    },

    onDeleteCancel() {
      this.deleteConfirmShow = false
      this.selectedForDelete = null
    },

    reset() {
      this.updateReset()
      this.delReset()
      this.createReset()
    },

    edit(item) {
      this.forceEditMode = true
      this.selectedUser = item['@id']
      this.showUserDialog = true
    },

    show(item) {
      this.forceEditMode = false
      this.selectedUser = item['@id']
      this.showUserDialog = true
    },

    getPaginatedPage(options) {
      let query =
        '/role_in_organizations?organization=' +
        this.organizationId +
        '&itemsPerPage=' +
        options.itemsPerPage +
        '&page=' +
        options.page

      if (options.sortBy.length > 0) {
        query = query + '&order[' + options.sortBy[0] + ']=' + (options.sortDesc[0] ? 'desc' : 'asc')
      }

      this.getPage(query)
    },

    ...mapActions({
      getPage: 'roleinorganization/list/default',
      deleteItem: 'roleinorganization/del/del',
      createReset: 'roleinorganization/create/reset',
      delReset: 'roleinorganization/del/reset',
      updateReset: 'roleinorganization/update/reset'
    })
  }
}
</script>
